import { Container, Typography, Box, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import AuthModal from '../components/auth/AuthModal';

const PricingPage = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingSubscription, setPendingSubscription] = useState<string | null>(null);


  // Debug logging (can be removed in production)
  console.log('🎯 PricingPage render:', { isAuthenticated, isLoading });

  // Handle navigation back from external sites
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Page became visible - user may have navigated back');
        // Small delay to ensure React has time to re-render
        setTimeout(() => {
          // Force a re-render by updating state
          setShowAuthModal(prev => prev);
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Simple approach: disable pricing table for unauthenticated users
  useEffect(() => {
    const timer = setTimeout(() => {
      const pricingTable = document.querySelector('stripe-pricing-table');
      if (pricingTable) {
        if (!isAuthenticated) {
          // Disable the pricing table for unauthenticated users
          (pricingTable as HTMLElement).style.pointerEvents = 'none';
          (pricingTable as HTMLElement).style.opacity = '0.6';
          console.log('🔒 Pricing table disabled for unauthenticated user');
        } else {
          // Enable the pricing table for authenticated users
          (pricingTable as HTMLElement).style.pointerEvents = 'auto';
          (pricingTable as HTMLElement).style.opacity = '1';
          console.log('✅ Pricing table enabled for authenticated user');
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // Handle post-authentication subscription flow
  useEffect(() => {
    if (isAuthenticated && pendingSubscription) {
      console.log('✅ User authenticated with pending subscription, clearing modal');
      setShowAuthModal(false);
      setPendingSubscription(null);

      // The user can now click the subscribe button again and it will work
      // We could also trigger a click programmatically, but letting them click is cleaner
    }
  }, [isAuthenticated, pendingSubscription]);

  const handleAuthClose = () => {
    setShowAuthModal(false);
    setPendingSubscription(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box>
        <Typography 
          variant="h5" 
          component="div"
          sx={{ 
            color: 'primary.main',
            fontWeight: 600,
            fontSize: { xs: '2rem', md: '2.5rem' },
            letterSpacing: 1.5,
            mb: 1,
            p: 4,
            textAlign: 'center',
            textTransform: 'uppercase'
          }}
        >
          Our Pricing Plans
        </Typography>
        <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 4 }}>
          Choose the plan that best fits your company's needs. Our flexible pricing is designed to scale with you.
        </Typography>

        {/* Compact banner for unauthenticated users - matches pricing table width */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          {!isAuthenticated && (
            <Box
              sx={{
                width: '100%',
                maxWidth: '1000px', // Same as pricing table
                backgroundColor: 'primary.main',
                color: 'white',
                p: 2.5,
                borderRadius: 2,
                textAlign: 'center',
                boxShadow: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                🔒 Sign Up Required
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                Create your account to access subscription plans
              </Typography>
              <Button
                variant="contained"
                size="medium"
                onClick={() => setShowAuthModal(true)}
                sx={{
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  borderRadius: 1.5,
                  backgroundColor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                🚀 Sign Up Now
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Box sx={{ width: '100%', maxWidth: '1000px', position: 'relative' }}>
            {/* Stripe pricing table */}
            <stripe-pricing-table
              pricing-table-id={import.meta.env.VITE_STRIPE_PRICING_TABLE_ID}
              publishable-key={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}
              {...({ 'client-reference-id': user?.id || undefined } as any)}
              {...({ 'customer-email': user?.email || undefined } as any)}
            />

            {/* No overlay needed - subscription clicks are intercepted directly */}
          </Box>
        </Box>

        {/* Removed duplicate CTA - using floating button instead */}
      </Box>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Need a fully managed search or help choosing a plan?
        </Typography>
        <Typography variant="body1" paragraph>
          Contact our sales team for personalized assistance in selecting the right plan for your business.
        </Typography>
        <a href="/contact" style={{ textDecoration: 'none' }}>
          <Box 
            component="button" 
            sx={{ 
              mt: 2, 
              px: 4, 
              py: 2, 
              backgroundColor: '#1976d2', 
              color: '#fff', 
              borderRadius: 2, 
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#115293' },
            }}
          >
            Contact Sales
          </Box>
        </a>
      </Box>

      {/* Floating action button for unauthenticated users - left side to avoid chatbot */}
      {!isAuthenticated && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => setShowAuthModal(true)}
            sx={{
              px: 3,
              py: 2,
              borderRadius: 3,
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: 6,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'scale(1.05)',
                boxShadow: 8,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            🚀 Sign Up
          </Button>
        </Box>
      )}

      {/* Authentication Modal */}
      <AuthModal
        open={showAuthModal}
        onClose={handleAuthClose}
        initialView="signup"
      />
    </Container>
  );
};

export default PricingPage;
